<template>
  <div class="main-table-wrapper">
    <el-table
      :data="data"
      :loading="loading"
      :row-class-name="getRowClassName"
      style="width: 100%">
      <el-table-column
        :label="$tc('form.name')">
        <template slot-scope="scope">
          {{ scope.row.name }}
        </template>
      </el-table-column>

      <el-table-column
        :label="$tc('club.club_name')">
        <template slot-scope="scope">
          <router-link v-if="scope.row.club" :to="{ name: 'ClubView', params: { id: scope.row.club.id } }" class="link">
            {{ scope.row.club.name }}
          </router-link>
        </template>
      </el-table-column>

      <el-table-column
        :label="$tc('form.email')">
        <template slot-scope="scope">
          {{ scope.row.email }}
        </template>
      </el-table-column>

      <el-table-column
        v-if="user.isAdmin"
        :label="$tc('general.actions')"
        align="right">
        <template slot-scope="scope">
          <template v-if="scope.row.id !== user.id">
            <el-dropdown trigger="click" @command="($event) => triggerAction($event, scope.row)">
              <el-button type="primary" icon="el-icon-more" circle />
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item :command="scope.row.isBlocked ? 'UNBLOCK' : 'BLOCK'">{{ scope.row.isBlocked ? $tc('general.unblock') : $tc('general.block') }}</el-dropdown-item>
                <el-dropdown-item :command="scope.row.isAdmin ? 'REMOVE_AS_ADMIN' : 'SET_AS_ADMIN'">{{ scope.row.isAdmin ? $tc('general.remove_as_admin') : $tc('general.set_as_admin') }}</el-dropdown-item>
                <el-dropdown-item command="PASSWORD">{{ $tc('general.update_password') }}</el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
          </template>
        </template>
      </el-table-column>
    </el-table>

    <el-dialog
      :title="$tc('general.update_password')"
      :visible="!!updatePasswordUserId"
      width="500px"
      @close="updatePasswordUserId = null"
    >
      <MainInput
        v-model="form.password"
        :validator="$v.form.password"
        prefix-icon="el-icon-key"
        :label="$tc('form.enter_password')"
        show-password />

      <div class="center">
        <el-button
          type="primary"
          class="mt-15"
          :loading="loading"
          @click="changePassword()">
          {{ $tc('general.update_password') }}
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { required, minLength } from 'vuelidate/lib/validators'

import errorHandler from '@/common/errorHandler.js'

import MainInput from '@/components/forms/Input.vue'

export default {
  components: {
    MainInput
  },

  props: {
    data: {
      type: Array,
      default: null
    },

    canEdit: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      form: {
        password: ''
      },
      loading: false,
      updatePasswordUserId: null
    }
  },

  computed: {
    user () {
      return this.$store.state.auth.user
    }
  },

  methods: {
    async changePassword () {
      this.$v.$touch()
      if (this.$v.form.$invalid) return

      this.loading = true
      try {
        await this.$store.dispatch('users/action', {
          id: this.updatePasswordUserId,
          action: 'changePassword',
          payload: { password: this.form.password }
        })
        this.form.password = null
        this.updatePasswordUserId = null
        this.$message.success(this.$tc('messages.success_changed_password'))
        this.$v.$reset()
      } catch (error) {
        this.error = error.response?.data?.error || this.$defaultErrorMessage
        console.log(error)
      } finally {
        this.loading = false
      }
    },
    getRowClassName ({ row }) {
      if (row.isAdmin) return 'admin'
      return row.isBlocked ? 'blocked' : ''
    },

    triggerAction (command, user) {
      if (command === 'PASSWORD') {
        this.form.password = ''
        this.updatePasswordUserId = user.id
      } else if (command === 'SET_AS_ADMIN') {
        this.setAsAdmin(user, true)
      } else if (command === 'REMOVE_AS_ADMIN') {
        this.setAsAdmin(user, false)
      } else if (command === 'BLOCK') {
        this.blockUser(user, true)
      } else if (command === 'UNBLOCK') {
        this.blockUser(user, false)
      }
    },

    async setAsAdmin (user, isAdmin) {
      const loading = this.$loading()
      try {
        await this.$store.dispatch('users/update', {
          id: user.id,
          payload: { isAdmin }
        })
        this.$message.success(this.$tc(isAdmin ? 'messages.success_set_as_admin' : 'messages.success_remove_as_admin'))
      } catch (e) {
        console.error(e)
        errorHandler(e)
      } finally {
        loading.close()
      }
    },

    async blockUser (user, isBlocked) {
      const loading = this.$loading()
      try {
        await this.$store.dispatch('users/update', {
          id: user.id,
          payload: { isBlocked }
        })
        this.$message.success(this.$tc(isBlocked ? 'messages.success_blocked_user' : 'messages.success_unblocked_user'))
      } catch (e) {
        console.error(e)
        errorHandler(e)
      } finally {
        loading.close()
      }
    },

    async beforeDelete (tournament) {
      try {
        await this.$confirm(this.$tc('tournament.ask_delete_tournament'))

        this.onDelete(tournament)
      } catch {}
    },

    async onDelete (tournament) {
      this.loading = true
      try {
        await this.$store.dispatch('tournaments/delete', tournament.id)
        this.$message.success(this.$tc('messages.success_deleted_tournament'))
      } catch (e) {
        console.error(e)
        errorHandler(e)
      } finally {
        this.loading = false
      }
    },

    openEdit (tournament) {
      this.$emit('edit', tournament)
    }
  },

  validations: {
    form: {
      password: { required, minLength: minLength(6) }
    }
  }
}
</script>

<style scoped>
.el-table >>> .admin {
  background-color: #ecfcea;
}
.el-table >>> .blocked {
  background-color: #ffeded;
}
</style>
